import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiBase, fetchUsingApi } from "../core/apiClient";

export interface HausingRoomMapping {
  householdId: number;
  householdTitle: string;
  roomId: string | null;
}

export interface HausingSettings {
  clientId: string | null;
  clientSecretConfigured: boolean;
  companyId: string | null;
  hausingBuildingId: string | null;
  ticketTitle: string | null;
  ticketDescription: string | null;
  syncDisabled: boolean;
  messageSyncDisabled: boolean;
  exposeSubmitterEmail: boolean;
  optOut: boolean;
  connectionConfigured: boolean;
  roomMappings: HausingRoomMapping[];
}

export interface UpdateHausingSettingsRequest {
  clientId: string;
  clientSecret?: string | null;
  companyId: string;
  hausingBuildingId: string;
  ticketTitle: string;
  ticketDescription: string;
  syncDisabled: boolean;
  messageSyncDisabled: boolean;
  exposeSubmitterEmail: boolean;
  optOut: boolean;
}

export interface HausingRoom {
  id: string;
  name: string;
}

interface HausingRoomsResponse {
  rooms: HausingRoom[];
}

export interface UpdateHausingRoomMappingRequest {
  roomId: string | null;
}

export const getHausingSettings = async (buildingId: number): Promise<HausingSettings> => {
  const response = await fetchUsingApi<HausingSettings>(
    `${apiBase}/configuration/v1/building/${buildingId}/hausing`,
    "GET",
    (data) => data as HausingSettings,
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to load Hausing settings");
  }

  return response.result;
};

export const updateHausingSettings = async (
  buildingId: number,
  request: UpdateHausingSettingsRequest,
): Promise<HausingSettings> => {
  const response = await fetchUsingApi<HausingSettings>(
    `${apiBase}/configuration/v1/building/${buildingId}/hausing`,
    "PUT",
    (data) => data as HausingSettings,
    JSON.stringify(request),
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to save Hausing settings");
  }

  return response.result;
};

export const getHausingRooms = async (buildingId: number): Promise<HausingRoom[]> => {
  const response = await fetchUsingApi<HausingRoomsResponse>(
    `${apiBase}/configuration/v1/building/${buildingId}/hausing/rooms`,
    "GET",
    (data) => data as HausingRoomsResponse,
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to load Hausing rooms");
  }

  return response.result.rooms;
};

export const updateHausingRoomMapping = async (
  buildingId: number,
  householdId: number,
  request: UpdateHausingRoomMappingRequest,
): Promise<void> => {
  const response = await fetchUsingApi<unknown>(
    `${apiBase}/configuration/v1/building/${buildingId}/hausing/households/${householdId}/room`,
    "PUT",
    () => undefined,
    JSON.stringify(request),
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to save Hausing room mapping");
  }
};

export const useHausingSettings = (buildingId: number) => {
  return useQuery({
    queryKey: ["hausing-settings", buildingId],
    queryFn: () => getHausingSettings(buildingId),
    enabled: buildingId > 0,
  });
};

export const useUpdateHausingSettings = (buildingId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateHausingSettingsRequest) => updateHausingSettings(buildingId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hausing-settings", buildingId] });
      queryClient.invalidateQueries({ queryKey: ["hausing-rooms", buildingId] });
    },
  });
};

export const useHausingRooms = (buildingId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ["hausing-rooms", buildingId],
    queryFn: () => getHausingRooms(buildingId),
    enabled: buildingId > 0 && enabled,
    retry: false,
  });
};

export const useUpdateHausingRoomMapping = (buildingId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, request }: { householdId: number; request: UpdateHausingRoomMappingRequest }) =>
      updateHausingRoomMapping(buildingId, householdId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hausing-settings", buildingId] });
    },
  });
};
